import React, { useState, useEffect } from "react";
import API from "../../utils/API";
import Swal from "sweetalert2";
import InputMask from "react-input-mask";
import { useSearchParams, useNavigate } from 'react-router-dom';

export default function ClientData(props) {
    const [client, setClient] = useState({
        cpf: "", name: "", phone_number: "", email: "",
        cep: "", address_street: "", address_state: "",
        address_number: "", address_neighborhood: "",
        address_extra_info: "", address_city: ""
    });

    const [errors, setErrors] = useState({});
    const navigate = useNavigate();
    const requiredFields = ["cpf", "name", "phone_number", "email"];

    // Document type: CPF (individuals) or CNPJ (companies). The value is always
    // stored in client.cpf; only the mask/validation differ.
    const [docType, setDocType] = useState("CPF");
    const cpfMask = "999.999.999-99";
    const cnpjMask = "99.999.999/9999-99";

    const [searchParams] = useSearchParams();
    const id = searchParams.get('id');
    const [vehicle, setVehicle] = useState(null);
    const [user, setUser] = useState(null);

    useEffect(() => {
        let retries = 10;
        const interval = setInterval(() => {
            const storedUserJSON = localStorage.getItem("yc_usr_dt_ls");
            if (storedUserJSON) {
                try {
                    const parsedUser = JSON.parse(storedUserJSON);
                    setUser(parsedUser);
                    // Mechanic and workshop chief (BOSS) must not have access to
                    // client data at all — bounce them away from this step.
                    if (parsedUser.role === "MECANICO" || parsedUser.role === "BOSS") {
                        navigate(id ? '/create/service?step=2&id=' + id : '/services');
                    }
                    clearInterval(interval);
                } catch (error) {
                    console.error("Failed to parse yc_usr_dt_ls:", error);
                    clearInterval(interval);
                }
            } else {
                retries--;
                if (retries <= 0) clearInterval(interval);
            }
        }, 500);
        return () => clearInterval(interval);
    }, [id]);

    useEffect(() => {
        if (id) {
            Swal.fire({ title: 'Carregando...', showConfirmButton: false, allowOutsideClick: false, didOpen: () => Swal.showLoading() });
            API.getUserByService(API.cancelToken(), id).then(res => {
                Swal.close();
                setVehicle({ model: res.data.model, year: res.data.year, license_plate: res.data.license_plate });
                // Auto-detect CNPJ (14 digits) vs CPF (11) from the stored value.
                if ((res.data.cpf || "").replace(/\D/g, "").length === 14) setDocType("CNPJ");
                setClient({
                    cpf: res.data.cpf || "", name: res.data.name || "",
                    phone_number: res.data.phone_number || "", email: res.data.email || "",
                    cep: res.data.cep || "", address_street: res.data.address_street || "",
                    address_state: res.data.state || "", address_number: res.data.number || "",
                    address_neighborhood: res.data.address_neighborhood || "",
                    address_extra_info: res.data.address_extra_info || "",
                    address_city: res.data.address_city || ""
                });
            }).catch(err => { Swal.close(); console.log(err); });
        }
        // No id → this is a brand-new service; the client step is the creator.
    }, [id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setClient(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
    };

    const validateForm = () => {
        const newErrors = {};
        let isValid = true;
        const labels = { cpf: docType, name: "Nome", phone_number: "Telefone", email: "Email" };
        requiredFields.forEach(field => {
            if (!client[field].trim()) { newErrors[field] = `${labels[field]} é obrigatório`; isValid = false; }
        });
        if (client.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(client.email)) { newErrors.email = "Email inválido"; isValid = false; }
        if (client.cpf) {
            const digits = client.cpf.replace(/\D/g, "");
            if (docType === "CPF" && digits.length !== 11) { newErrors.cpf = "CPF inválido"; isValid = false; }
            if (docType === "CNPJ" && digits.length !== 14) { newErrors.cpf = "CNPJ inválido"; isValid = false; }
        }
        setErrors(newErrors);
        return isValid;
    };

    const handleBlur = (e) => {
        const { name, value } = e.target;
        const docLen = value.replace(/[^0-9]/g, '').length;
        if (name === "cpf" && (docLen === 11 || docLen === 14)) {
            Swal.fire({ title: 'Buscando cliente...', showConfirmButton: false, allowOutsideClick: false, didOpen: () => Swal.showLoading() });
            API.getClient(API.cancelToken(), value).then(res => {
                setClient({
                    cpf: res.data.cpf, name: res.data.name, phone_number: res.data.phone_number,
                    email: res.data.email, cep: res.data.cep, address_street: res.data.address_street,
                    address_state: res.data.address_state, address_number: res.data.address_number,
                    address_neighborhood: res.data.address_neighborhood, address_extra_info: res.data.address_extra_info,
                    address_city: res.data.address_city
                });
                Swal.close();
            }).catch(() => Swal.close());
        }
        if (name === "cep" && value.replace(/[^0-9]/g, '').length === 8) {
            Swal.fire({ title: 'Buscando CEP...', showConfirmButton: false, allowOutsideClick: false, didOpen: () => Swal.showLoading() });
            API.getAddress(API.cancelToken(), value).then(res => {
                setClient(prev => ({
                    ...prev, cep: res.data.cep, address_street: res.data.logradouro,
                    address_state: res.data.uf, address_neighborhood: res.data.bairro, address_city: res.data.localidade
                }));
                Swal.close();
            }).catch(() => Swal.close());
        }
    };

    const postClient = () => {
        if (!validateForm()) {
            Swal.fire({ title: 'Erro', text: 'Preencha todos os campos obrigatórios corretamente.', icon: 'error', confirmButtonText: 'OK' });
            return;
        }
        Swal.fire({ title: 'Salvando cliente...', showConfirmButton: false, allowOutsideClick: false, didOpen: () => Swal.showLoading() });
        API.postClient(API.cancelToken(), client).then(res => {
            Swal.close();
            localStorage.setItem('client', JSON.stringify(client));
            localStorage.setItem('client_id', JSON.stringify(res.data.client_id));
            // Client-first: step 2 creates the service row (when no id yet) and
            // returns the service_id. Then advance to the Vehicle step.
            API.postServiceStep2(API.cancelToken(), res.data.client_id, id).then((res2) => {
                const newServiceId = res2?.data?.service_id || id;
                navigate('/create/service?step=2&id=' + newServiceId);
            });
        }).catch(err => {
            console.log(err); Swal.close();
            Swal.fire({ title: 'Erro', text: 'Ocorreu um erro ao salvar os dados do cliente.', icon: 'error', confirmButtonText: 'OK' });
        });
    };

    const getMask = (key) => {
        if (key === "cpf") return "999.999.999-99";
        if (key === "phone_number") return "(99) 99999-9999";
        if (key === "cep") return "99999-999";
        return null;
    };

    const inputClass = (field) =>
        `w-full px-3 py-2.5 border-2 rounded-lg text-sm bg-white transition-all focus:outline-none focus:ring-2 focus:ring-[#389E0D] focus:border-transparent ${
            errors[field] ? 'border-red-300 bg-red-50' : 'border-gray-200'
        } ${(user?.role === "COMPRADOR" || user?.role === "MECANICO") ? 'bg-gray-50 cursor-not-allowed' : ''}`;

    return (
        <div className="flex flex-col items-center w-full bg-white rounded-lg shadow-sm">
            {/* Header */}
            <div className="w-full px-8 pt-6 pb-4 border-b border-gray-100 max-md:px-5">
                <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-[#389E0D] bg-opacity-10 flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#389E0D]" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                        </svg>
                    </div>
                    <div>
                        <h2 className="text-xl font-semibold text-gray-900">Dados do Cliente</h2>
                        <p className="text-sm text-gray-500">Preencha as informações do cliente para prosseguir</p>
                    </div>
                </div>
            </div>

            <div className="flex flex-col lg:flex-row w-full">
                {/* Vehicle Summary Sidebar */}
                {vehicle && (
                    <div className="lg:w-72 flex-shrink-0 border-b lg:border-b-0 lg:border-r border-gray-100 p-6 bg-gray-50 max-md:px-5">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Veículo</h3>
                            <button
                                onClick={() => navigate('/create/service?step=1&id=' + id)}
                                className="text-xs font-medium text-[#389E0D] hover:underline"
                            >
                                Editar
                            </button>
                        </div>
                        <div className="space-y-3">
                            <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                    <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                                    <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1v-1h3.05a2.5 2.5 0 014.9 0H19a1 1 0 001-1v-2a4 4 0 00-4-4h-3V5a1 1 0 00-1-1H3zm9 4h3l2 2v1h-5V8z" />
                                </svg>
                                <div>
                                    <p className="text-sm font-medium text-gray-900">{vehicle.model}</p>
                                    <p className="text-xs text-gray-400">{vehicle.year}</p>
                                </div>
                            </div>
                            <div className="flex items-center justify-center p-2.5 bg-white rounded-lg border border-gray-200">
                                <span className="font-mono text-sm font-bold text-gray-700 tracking-widest">{vehicle.license_plate}</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Client Form */}
                <div className="flex-1 p-6 lg:p-8 max-md:px-5">
                    {/* Document (CPF / CNPJ) - highlighted */}
                    <div className="mb-6 p-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                        <div className="flex items-center justify-between mb-2">
                            <label className="block text-sm font-semibold text-gray-700">
                                {docType} do Cliente <span className="text-red-500">*</span>
                            </label>
                            {!(user?.role === "COMPRADOR" || user?.role === "MECANICO") && (
                                <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden text-xs font-medium">
                                    <button
                                        type="button"
                                        onClick={() => { setDocType("CPF"); setClient(prev => ({ ...prev, cpf: "" })); setErrors(prev => ({ ...prev, cpf: "" })); }}
                                        className={`px-3 py-1.5 ${docType === "CPF" ? "bg-[#389E0D] text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                                    >
                                        CPF
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => { setDocType("CNPJ"); setClient(prev => ({ ...prev, cpf: "" })); setErrors(prev => ({ ...prev, cpf: "" })); }}
                                        className={`px-3 py-1.5 ${docType === "CNPJ" ? "bg-[#389E0D] text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                                    >
                                        CNPJ
                                    </button>
                                </div>
                            )}
                        </div>
                        <div className="flex gap-3 items-end">
                            <div className="flex-1">
                                <InputMask
                                    className={`${inputClass('cpf')} font-mono text-lg tracking-wider`}
                                    mask={docType === "CNPJ" ? cnpjMask : cpfMask}
                                    maskChar={null}
                                    value={client.cpf}
                                    placeholder={docType === "CNPJ" ? "00.000.000/0000-00" : "000.000.000-00"}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    name="cpf"
                                    readOnly={(user?.role === "COMPRADOR" || user?.role === "MECANICO")}
                                />
                            </div>
                        </div>
                        {errors.cpf && <p className="mt-1.5 text-sm text-red-600">{errors.cpf}</p>}
                    </div>

                    {/* Personal info section */}
                    <div className="mb-6">
                        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                            </svg>
                            Informações Pessoais
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-600 mb-1.5">Nome <span className="text-red-500">*</span></label>
                                <input className={inputClass('name')} type="text" name="name" placeholder="Nome completo" value={client.name} onChange={handleChange} readOnly={(user?.role === "COMPRADOR" || user?.role === "MECANICO")} />
                                {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-600 mb-1.5">Email <span className="text-red-500">*</span></label>
                                <input className={inputClass('email')} type="email" name="email" placeholder="email@exemplo.com" value={client.email} onChange={handleChange} readOnly={(user?.role === "COMPRADOR" || user?.role === "MECANICO")} />
                                {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-600 mb-1.5">Telefone <span className="text-red-500">*</span></label>
                                <InputMask className={inputClass('phone_number')} mask="(99) 99999-9999" maskChar={null} name="phone_number" placeholder="(11) 99999-9999" value={client.phone_number} onChange={handleChange} readOnly={(user?.role === "COMPRADOR" || user?.role === "MECANICO")} />
                                {errors.phone_number && <p className="mt-1 text-sm text-red-600">{errors.phone_number}</p>}
                            </div>
                        </div>
                    </div>

                    {/* Address section */}
                    <div>
                        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                            </svg>
                            Endereço
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-600 mb-1.5">CEP</label>
                                <InputMask className={inputClass('cep')} mask="99999-999" maskChar={null} name="cep" placeholder="00000-000" value={client.cep} onChange={handleChange} onBlur={handleBlur} readOnly={(user?.role === "COMPRADOR" || user?.role === "MECANICO")} />
                                <p className="text-xs text-gray-400 mt-1">Preenche o endereço automaticamente</p>
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-600 mb-1.5">Rua</label>
                                <input className={inputClass('address_street')} type="text" name="address_street" placeholder="Rua / Avenida" value={client.address_street} onChange={handleChange} readOnly={(user?.role === "COMPRADOR" || user?.role === "MECANICO")} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-600 mb-1.5">Número</label>
                                <input className={inputClass('address_number')} type="text" name="address_number" placeholder="Nº" value={client.address_number} onChange={handleChange} readOnly={(user?.role === "COMPRADOR" || user?.role === "MECANICO")} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-600 mb-1.5">Bairro</label>
                                <input className={inputClass('address_neighborhood')} type="text" name="address_neighborhood" placeholder="Bairro" value={client.address_neighborhood} onChange={handleChange} readOnly={(user?.role === "COMPRADOR" || user?.role === "MECANICO")} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-600 mb-1.5">Complemento</label>
                                <input className={inputClass('address_extra_info')} type="text" name="address_extra_info" placeholder="Apto, bloco..." value={client.address_extra_info} onChange={handleChange} readOnly={(user?.role === "COMPRADOR" || user?.role === "MECANICO")} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-600 mb-1.5">Estado</label>
                                <input className={inputClass('address_state')} type="text" name="address_state" placeholder="UF" value={client.address_state} onChange={handleChange} readOnly={(user?.role === "COMPRADOR" || user?.role === "MECANICO")} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-600 mb-1.5">Cidade</label>
                                <input className={inputClass('address_city')} type="text" name="address_city" placeholder="Cidade" value={client.address_city} onChange={handleChange} readOnly={(user?.role === "COMPRADOR" || user?.role === "MECANICO")} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className="w-full px-8 py-5 border-t border-gray-100 flex justify-between max-md:px-5">
                <button
                    onClick={() => navigate('/create/service?step=1&id=' + id)}
                    className="inline-flex items-center gap-2 px-5 py-2.5 border-2 border-gray-200 text-gray-600 font-medium text-sm rounded-lg hover:bg-gray-50 transition-colors"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                    </svg>
                    Voltar
                </button>
                {user?.role !== "COMPRADOR" && user?.role !== "MECANICO" && (
                    <button
                        onClick={postClient}
                        className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#389E0D] text-white font-medium text-sm rounded-lg hover:bg-green-700 transition-colors shadow-sm"
                    >
                        Prosseguir
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                    </button>
                )}
            </div>
        </div>
    );
}
